import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  getCustomSetting,
  updateSetting,
  updateUIConfig,
} from '@/features/auth/apis/setting'
import { Setting, UIConfig, UIConfigKey } from '@/features/auth/types/setting'

// ========================================
// useSettingQuery
// ========================================

/**
 * 設定取得クエリ（TanStack Query）
 *
 * 責務:
 * - ユーザー設定の取得
 * - ログイン状態に応じた有効化制御
 *
 * 使用例:
 * ```tsx
 * const { data: setting, isLoading } = useSettingQuery({ enabled: isLoggedIn })
 * ```
 */
export const useSettingQuery = (options: { enabled?: boolean } = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: queryKeys.setting.all,
    queryFn: async (): Promise<Setting> => {
      const { data } = await getCustomSetting()
      return data
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })
}

// ========================================
// useUpdateSettingMutation
// ========================================

interface UpdateSettingVariables {
  key: keyof Setting
  value: Setting[keyof Setting]
}

/**
 * 設定更新Mutation（TanStack Query）
 *
 * 責務:
 * - 設定の部分更新
 * - 成功時に設定キャッシュをinvalidate
 *
 * 使用例:
 * ```tsx
 * const updateMutation = useUpdateSettingMutation()
 * await updateMutation.mutateAsync({ key: 'theme', value: 'dark' })
 * ```
 */
export const useUpdateSettingMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ key, value }: UpdateSettingVariables) => {
      await updateSetting(key, value)
    },
    onSuccess: () => {
      // 設定キャッシュをinvalidate
      queryClient.invalidateQueries({
        queryKey: queryKeys.setting.all,
      })
    },
  })
}

// ========================================
// useUpdateUIConfigMutation
// ========================================

interface UpdateUIConfigVariables {
  key: UIConfigKey
  value: UIConfig[UIConfigKey]
}

/**
 * UI 表示嗜好 (ui_config) 更新 Mutation
 *
 * 責務:
 * - font_size / improvements_expanded の単一更新 (server 永続化)
 * - 成功時に設定キャッシュを invalidate
 *
 * back (UserUIConfig) 未デプロイ時は PATCH が 400 を返すが、 呼び出し側は
 * 楽観更新 + localStorage / local state で UX を担保する (server 永続化のみ no-op).
 */
export const useUpdateUIConfigMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ key, value }: UpdateUIConfigVariables) => {
      await updateUIConfig(key, value)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.setting.all,
      })
    },
  })
}
